import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, EmptyState } from "../components/UI";
import { toast } from "sonner";
import {
  AlertTriangle, Clock, CheckCircle2, ClipboardList, Calendar, ArrowRight, Filter,
} from "lucide-react";

/*
 * MonitoringDashboard , 30/60/90 day corrective-action follow-ups.
 * Reachable via HR Command &raquo; Monitoring (and Workspace &raquo; Monitoring for
 * Company Admin). Investigators (managers assigned to a case) also see this
 * page but scoped to their own case queue only.
 */

const STATUS_META = {
  overdue:   { label: "Overdue",   badge: "bg-red-50 text-red-700 border-red-200",       row: "border-red-300 bg-red-50/40 hover:border-red-500" },
  due:       { label: "Due today", badge: "bg-amber-50 text-amber-800 border-amber-300", row: "border-amber-300 bg-amber-50/40 hover:border-amber-500" },
  upcoming:  { label: "Upcoming",  badge: "bg-blue-50 text-blue-700 border-blue-200",    row: "border-slate-200 bg-white hover:border-blue-500" },
  scheduled: { label: "Scheduled", badge: "bg-slate-50 text-slate-600 border-slate-200", row: "border-slate-200 bg-white hover:border-slate-400" },
};

export default function MonitoringDashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ overdue: 0, due: 0, upcoming: 0, scheduled: 0 });
  const [filter, setFilter] = useState("all");
  const [busyKey, setBusyKey] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");
  // Bulk-select state
  const [selected, setSelected] = useState(new Set());
  const [bulkNote, setBulkNote] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/monitoring/upcoming");
      setItems(data.items || []);
      setCounts(data.counts || { overdue: 0, due: 0, upcoming: 0, scheduled: 0 });
    } catch { toast.error("Failed to load monitoring milestones"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "actionable") return items.filter((r) => ["overdue", "due"].includes(r.status));
    return items.filter((r) => r.status === filter);
  }, [items, filter]);

  const complete = async (r, withNote) => {
    const key = `${r.case_id}-${r.days}`;
    setBusyKey(key);
    try {
      await api.post(`/monitoring/case/${r.case_id}/complete/${r.days}`, { note: withNote || null });
      toast.success(`${r.days}-day check-in complete for ${r.case_number}`);
      setNoteFor(null);
      setNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusyKey(null);
    }
  };

  const toggleSelect = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected(new Set(filtered.map((r) => `${r.case_id}-${r.days}`)));
  };

  const clearSelection = () => setSelected(new Set());

  const bulkComplete = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const bulkItems = filtered
        .filter((r) => selected.has(`${r.case_id}-${r.days}`))
        .map((r) => ({ case_id: r.case_id, days: r.days }));
      const { data } = await api.post("/monitoring/complete/bulk", {
        items: bulkItems,
        note: bulkNote || null,
      });
      const completed = data?.completed?.length || 0;
      const skipped = data?.skipped?.length || 0;
      toast.success(
        skipped > 0
          ? `Marked ${completed} milestone${completed === 1 ? "" : "s"} complete (${skipped} skipped)`
          : `Marked ${completed} milestone${completed === 1 ? "" : "s"} complete`
      );
      clearSelection();
      setBulkNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk completion failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="monitoring-dashboard">
      <SectionHeader
        title="Corrective-action monitoring"
        subtitle="30, 60, and 90-day check-ins on cases that closed with a corrective-action plan. Overdue milestones are highlighted; completing one writes an audit-log entry."
      />

      {/* Stat strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6 mb-6">
        <StatCard
          Icon={AlertTriangle}
          label="Overdue"
          value={counts.overdue}
          tone="text-red-700 bg-red-50 border-red-200"
          testid="stat-overdue"
          onClick={() => setFilter("overdue")}
          active={filter === "overdue"}
        />
        <StatCard
          Icon={Clock}
          label="Due today"
          value={counts.due}
          tone="text-amber-800 bg-amber-50 border-amber-300"
          testid="stat-due"
          onClick={() => setFilter("due")}
          active={filter === "due"}
        />
        <StatCard
          Icon={Calendar}
          label="Upcoming (7d)"
          value={counts.upcoming}
          tone="text-blue-700 bg-blue-50 border-blue-200"
          testid="stat-upcoming"
          onClick={() => setFilter("upcoming")}
          active={filter === "upcoming"}
        />
        <StatCard
          Icon={ClipboardList}
          label="Scheduled"
          value={counts.scheduled}
          tone="text-slate-700 bg-slate-50 border-slate-200"
          testid="stat-scheduled"
          onClick={() => setFilter("scheduled")}
          active={filter === "scheduled"}
        />
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1">
          <Filter size={10} strokeWidth={2}/> Filter
        </span>
        {["all", "actionable", "overdue", "due", "upcoming", "scheduled"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={
              "text-xs px-2.5 py-1 border rounded-full " +
              (filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700")
            }
            data-testid={`monitor-filter-${f}`}
          >
            {f}
          </button>
        ))}
        {filtered.length > 0 && (
          <button
            onClick={selectAllVisible}
            className="text-[11px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 px-2 py-1"
            data-testid="monitor-select-all"
          >
            Select all visible
          </button>
        )}
        <span className="text-[10px] font-mono text-slate-400 ml-auto">{filtered.length} milestones</span>
      </div>

      {/* Bulk actions bar , appears when at least one row is selected */}
      {selected.size > 0 && (
        <div
          className="sticky top-16 z-30 border border-blue-300 bg-blue-50 rounded p-3 mb-3 flex items-center gap-3 flex-wrap shadow-sm"
          data-testid="monitor-bulk-bar"
        >
          <span className="text-xs font-mono uppercase tracking-widest text-blue-800 shrink-0">
            {selected.size} selected
          </span>
          <input
            type="text"
            value={bulkNote}
            onChange={(e) => setBulkNote(e.target.value)}
            placeholder="Shared check-in note (optional)…"
            className="input !py-1 !text-xs flex-1 min-w-[220px]"
            data-testid="monitor-bulk-note"
          />
          <button
            className="btn-primary text-xs"
            onClick={bulkComplete}
            disabled={bulkBusy}
            data-testid="monitor-bulk-complete"
          >
            <CheckCircle2 size={12} strokeWidth={2}/>
            {bulkBusy ? "Completing…" : `Complete ${selected.size}`}
          </button>
          <button
            className="btn-ghost text-xs"
            onClick={clearSelection}
            data-testid="monitor-bulk-clear"
          >
            Clear
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title={filter === "all" ? "No monitoring items yet" : `No ${filter} milestones`}
          subtitle="Cases with an approved findings or a closed status will show 30, 60, and 90-day check-ins here automatically."
        />
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.scheduled;
            const key = `${r.case_id}-${r.days}`;
            const showNoteBox = noteFor === key;
            const isSelected = selected.has(key);
            return (
              <div key={key} className={"border p-4 rounded transition-all " + meta.row + (isSelected ? " ring-2 ring-blue-500" : "")} data-testid={`milestone-${r.case_number}-${r.days}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(key)}
                      className="mt-1 shrink-0 h-4 w-4 accent-blue-600"
                      data-testid={`milestone-select-${r.case_number}-${r.days}`}
                      aria-label={`Select ${r.days}-day check-in for ${r.case_number}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={"inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 " + meta.badge}>
                          {r.status === "overdue" && <AlertTriangle size={10} strokeWidth={2.5}/>}
                          {r.status === "due" && <Clock size={10} strokeWidth={2.5}/>}
                          {r.status === "upcoming" && <Calendar size={10} strokeWidth={2}/>}
                          {r.status === "scheduled" && <ClipboardList size={10} strokeWidth={2}/>}
                          {meta.label}
                        </span>
                        <span className="text-[10px] font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">{r.days}-day</span>
                        <span className="text-[10px] font-mono text-slate-500">{r.case_number}</span>
                        <span className={"pill pill-" + r.priority}>{r.priority}</span>
                      </div>
                      <div className="font-semibold text-slate-900">{r.title}</div>
                      <div className="text-[11px] font-mono text-slate-500 mt-1">
                        due {new Date(r.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        {" · "}assignee {r.assignee_name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Link to={`/cases/${r.case_id}`} className="btn-ghost text-xs" data-testid={`milestone-open-case-${r.case_number}-${r.days}`}>
                      Open case <ArrowRight size={11} strokeWidth={2}/>
                    </Link>
                    <button
                      onClick={() => setNoteFor(showNoteBox ? null : key)}
                      className="btn-primary text-xs"
                      data-testid={`milestone-complete-${r.case_number}-${r.days}`}
                    >
                      <CheckCircle2 size={12} strokeWidth={2}/> Mark complete
                    </button>
                  </div>
                </div>
                {showNoteBox && (
                  <div className="mt-3 border-t border-slate-200 pt-3">
                    <label className="eyebrow block mb-1">Check-in note (optional)</label>
                    <textarea
                      rows={2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      className="input mb-2"
                      placeholder="Summarize what you observed during the check-in…"
                      data-testid={`milestone-note-${r.case_number}-${r.days}`}
                    />
                    <div className="flex justify-end gap-2">
                      <button className="btn-ghost text-xs" onClick={() => { setNoteFor(null); setNote(""); }}>Cancel</button>
                      <button
                        className="btn-primary text-xs"
                        onClick={() => complete(r, note)}
                        disabled={busyKey === key}
                        data-testid={`milestone-confirm-${r.case_number}-${r.days}`}
                      >
                        {busyKey === key ? "Saving…" : "Confirm completion"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 text-[11px] text-slate-500 font-mono">
        Monitoring is available to managers, HR, company admins, and super admins. Managers only see cases assigned to them. Completing a milestone writes an audit_log entry (`action=monitoring_checkin`) and a persistent `monitoring_checkins` row.
        {user?.role === "manager" && <div className="mt-1">Scope: cases you are assigned to.</div>}
      </div>
    </div>
  );
}

function StatCard({ Icon, label, value, tone, testid, onClick, active }) {
  return (
    <button
      onClick={onClick}
      className={
        "text-left border p-4 rounded transition-all " +
        tone +
        (active ? " ring-2 ring-offset-1 ring-slate-900" : "")
      }
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
        <Icon size={11} strokeWidth={2}/> {label}
      </div>
      <div className="font-display text-3xl font-semibold mt-2">{value}</div>
    </button>
  );
}
